import { FC, useCallback, useEffect, useState } from "react";
import NewTooltip from "../atoms/NewTooltip";
import { HiOutlineTrash } from "react-icons/hi2";
import { validateField, FormatType } from "../../services/validationService";
import "./CopyPasteTable.css";

export type HeaderTable = {
    label: string;
    field: string;
    required: boolean;
    format: string;
    size?: number;
};

type TableResult = {
    line: number;
    result: "success" | "error";
    message: string;
};

interface CopyPasteTableProps {
    fixedHeaders: HeaderTable[];
    allowDuplicates?: boolean;
    /** Mapa de "NOME DA COLUNA COLADA" (uppercase) -> field de fixedHeaders. Quando informado, a
     * primeira linha colada é tratada como cabeçalho e os dados são reordenados automaticamente
     * para bater com fixedHeaders, mesmo que a ordem/nome das colunas de origem seja diferente
     * (ex: cada corretora exporta as planilhas com nomes de coluna distintos). */
    columnMapping?: Record<string, string>;
    /** Quando true, cada colagem ADICIONA linha(s) às já existentes em vez de substituir a
     * tabela inteira (default: false, comportamento padrão). Usado quando a fonte é 1 bloco de
     * dados por operação (não uma planilha inteira de uma vez) - ver `parseBlockPaste`. Mantém
     * a área de colagem sempre visível (não só quando a tabela está vazia). */
    appendMode?: boolean;
    /** Parser alternativo pra colagem em BLOCO (não tabular - ex: "memória de cálculo" de uma
     * única operação, label:valor em vez de cabeçalho+linhas). Quando informado, é tentado
     * ANTES do parsing tabular padrão: se reconhecer o formato colado, retorna um único registro
     * (chave = field de fixedHeaders) que vira uma linha nova; se não reconhecer (formato
     * inesperado), retorna null e `onBlockParseError` é chamado - a colagem tabular padrão NÃO
     * roda como fallback (evita interpretar um bloco malformado como se fosse uma planilha). */
    parseBlockPaste?: (rows: string[][]) => Record<string, string> | null;
    /** Chamado quando `parseBlockPaste` está definido mas não reconheceu o que foi colado. */
    onBlockParseError?: () => void;
}

const CopyPasteTable: FC<CopyPasteTableProps> = ({
    fixedHeaders = [], allowDuplicates = false, columnMapping, appendMode = false, parseBlockPaste, onBlockParseError,
}) => {

    const [tableData, setTableData] = useState<string[][]>([]);
    const [draggingColumn, setDraggingColumn] = useState<number | null>(null);
    const [headers, setHeaders] = useState(fixedHeaders);
    // Só usado em appendMode: a textarea some após a 1ª colagem nos demais fluxos,
    // mas aqui ela precisa continuar visível pra receber a próxima colagem - por
    // isso vira controlada, pra poder ser limpa a cada Ctrl+V bem-sucedido em vez
    // de reter visualmente o texto colado anteriormente.
    const [pasteBuffer, setPasteBuffer] = useState("");

    const maxColumns = Math.max(headers.length, ...tableData.map(row => row.length));
    const tableHeight = "calc(100vh - 400px)";

    const [errors, setErrors] = useState<TableResult[]>([]);

    useEffect(() => {

        window.addEventListener("CopyPasteTable:clearData", clearData);
        window.addEventListener("CopyPasteTable:setErrors", (event: Event) => setErrors((event as CustomEvent).detail));

        return () => {
            window.removeEventListener("CopyPasteTable:clearData", clearData);
        };

    }, []);

    const validateColumns = () => {
        const errorList: TableResult[] = [];
        const documentValues: string[] = [];
        const duplicateIndexes: number[] = [];

        tableData.forEach((row, rowIndex) => {
            headers.forEach((header, colIndex) => {
                const value = row[colIndex]?.trim();

                if (header.required && !value) {
                    errorList.push({
                        line: rowIndex,
                        result: "error",
                        message: `Coluna "${header.label}" é obrigatória.`,
                    });
                    return;
                }

                if (value && header.format) {
                    const validation = validateField(value, header.format as FormatType, header.label, header.size);
                    if (!validation.isValid) {
                        errorList.push({
                            line: rowIndex,
                            result: "error",
                            message: validation.message!,
                        });
                    }
                }

                // Verificar duplicados no primeiro campo (documento) apenas se não permitir duplicatas
                if (!allowDuplicates && colIndex === 0 && value) {
                    if (documentValues.includes(value)) {
                        duplicateIndexes.push(rowIndex);
                        const firstIndex = documentValues.indexOf(value);
                        if (!duplicateIndexes.includes(firstIndex)) {
                            duplicateIndexes.push(firstIndex);
                        }
                    }
                    documentValues.push(value);
                }
            });
        });

        // Adicionar erros para documentos duplicados apenas se não permitir duplicatas
        if (!allowDuplicates) {
            duplicateIndexes.forEach(index => {
                errorList.push({
                    line: index,
                    result: "error",
                    message: "Documento duplicado encontrado.",
                });
            });
        }

        setErrors(errorList);
        window.dispatchEvent(new CustomEvent("CopyPasteTable:errors", { detail: errorList }));
    };

    const clearData = () => {
        setTableData([]);
        setErrors([]);
    };

    const mapPastedRows = (filteredRows: string[][]): string[][] => {
        if (!columnMapping || filteredRows.length === 0) return filteredRows;

        // Primeira linha colada é tratada como cabeçalho de origem (nomes da corretora)
        const pastedHeaders = filteredRows[0].map(h => h.toUpperCase());
        const dataRows = filteredRows.slice(1);

        // Quando duas (ou mais) colunas da planilha colada mapeiam pro mesmo
        // campo, guarda TODAS as ocorrências (não só a primeira) - a escolha
        // entre elas é feita por LINHA logo abaixo, não uma vez só pra tabela
        // inteira. Cobre dois casos reais distintos:
        // - Genial manda duas colunas "FINANCEIRO" (bruta e desagiada) - a
        //   primeira sempre tem valor, então sempre vence (comportamento
        //   preservado).
        // - XP manda taxa/taxa cheia em colunas diferentes conforme o papel
        //   (PRE vs CDI) - sempre uma das duas vem zerada, dependendo do
        //   indexador de CADA linha (bug real de produção, 2026-09-15).
        const fieldToIndices: Record<string, number[]> = {};
        pastedHeaders.forEach((header, index) => {
            const field = columnMapping[header];
            if (!field) return;
            (fieldToIndices[field] ??= []).push(index);
        });

        // Usa a ordem ATUAL de `headers` (não `fixedHeaders`) - o usuário pode
        // arrastar colunas antes de colar (fluxo de fallback sem mapeamento
        // automático) e a tabela renderiza/serializa (genereateJSonData) pela
        // ordem de `headers`; mapear pela ordem original desalinhava os valores
        // com os campos depois de um reorder + nova colagem mapeada (bug real).
        return dataRows.map(row =>
            headers.map(header => {
                const indices = fieldToIndices[header.field];
                if (!indices || indices.length === 0) return '';
                // Primeiro valor não-vazio/não-zero entre as colunas candidatas
                // nesta linha; se todas estiverem vazias/zeradas, usa a primeira
                // (mantém o comportamento anterior quando não há ambiguidade real).
                // Reconhece zero em qualquer formato numérico colado ("0", "0,00",
                // "0.00", "0,00%") - não só a string exata "0" (achado real do
                // Copilot review: "0,00"/"0.00" passavam como "valor real").
                const withValue = indices.find(index => {
                    const value = (row[index] || '').trim();
                    if (value === '') return false;
                    const numeric = parseFloat(value.replace('%', '').replace(',', '.'));
                    return Number.isNaN(numeric) || numeric !== 0;
                });
                return row[withValue ?? indices[0]] || '';
            }),
        );
    };

    // Converte o clipboard (HTML de planilha colada via email/webmail, ou texto
    // tab-separated do Excel/CSV) numa grade de células - extraído de dentro do
    // handler de paste pra ser reaproveitado tanto no fluxo tabular padrão quanto
    // no fluxo de colagem em bloco (`parseBlockPaste`).
    const clipboardToRows = (event: React.ClipboardEvent<HTMLTextAreaElement>): string[][] => {
        const htmlData = event.clipboardData.getData("text/html");
        const textData = event.clipboardData.getData("text");

        let rows: string[][] = [];

        // Estratégia 1: parsear HTML (funciona com planilhas coladas via email/webmail)
        if (htmlData) {
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(htmlData, "text/html");
                const table = doc.querySelector("table");

                if (table) {
                    const htmlMatrix = Array.from(table.querySelectorAll("tr")).map(row =>
                        Array.from(row.querySelectorAll("td, th")).map(cell => cell.textContent?.trim() || ""),
                    );

                    if (htmlMatrix.length > 0 && htmlMatrix.some(r => r.length > 1)) {
                        rows = htmlMatrix;
                    }
                }
            } catch {
                rows = [];
            }
        }

        // Estratégia 2: fallback para texto (Excel/CSV, separado por tab)
        if (rows.length === 0 && textData) {
            rows = textData.split("\n")
                .map(row =>
                    row
                        .split("\t")
                        .map(cell =>
                            cell
                                .replace(/\u00A0/g, " ") // remove espaço não separável (nbsp)
                                .replace(/\s+/g, " ")    // normaliza múltiplos espaços
                                .trim(),
                        ),
                );
        }

        return rows.filter(row => row.some(cell => cell.trim() !== ""));
    };

    const handlePaste = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const filteredRows = clipboardToRows(event);

        if (parseBlockPaste) {
            // Colagem em bloco (1 operação por Ctrl+V, ex: Nikos) nunca cai no
            // parsing tabular como fallback: um bloco não reconhecido não é uma
            // planilha, tentar interpretar como tal só geraria linha lixo.
            event.preventDefault();
            const parsed = parseBlockPaste(filteredRows);
            if (!parsed) {
                onBlockParseError?.();
                return;
            }
            const newRow = headers.map(header => parsed[header.field] ?? "");
            setTableData(prevData => appendMode ? [...prevData, newRow] : [newRow]);
            setPasteBuffer("");
            return;
        }

        const mapped = mapPastedRows(filteredRows);
        setTableData(prevData => appendMode ? [...prevData, ...mapped] : mapped);
        setPasteBuffer("");
    }, [columnMapping, fixedHeaders, headers, appendMode, parseBlockPaste, onBlockParseError]);

    useEffect(() => {
        validateColumns();
    }, [tableData, headers]);

    const handleDragStart = (index: number) => {
        setDraggingColumn(index);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>, index: number) => {
        event.preventDefault();
        if (draggingColumn !== null && draggingColumn !== index) {
            const newHeaders = [...headers];
            const [movedHeader] = newHeaders.splice(draggingColumn, 1);
            newHeaders.splice(index, 0, movedHeader);
            setHeaders(newHeaders);
            setDraggingColumn(index);
        }
    };

    const handleDragEnd = () => {
        setDraggingColumn(null);
        genereateJSonData();
    };

    const removeColumn = (colIndex: number) => {
        setTableData(prevData => prevData.map(row => row.filter((_, i) => i !== colIndex)));
    };

    const removeRow = (rowIndex: number) => {
        setTableData(prevData => prevData.filter((_, i) => i !== rowIndex));
    };

    useEffect(() => {
        genereateJSonData();
    }, [tableData]);

    const genereateJSonData = () => {
        const data = tableData.map(row =>
            headers.reduce((acc, header, colIndex) => {
                acc[header.field] = row[colIndex] || "";
                return acc;
            }, {} as Record<string, string>),
        );

        window.dispatchEvent(new CustomEvent("CopyPasteTable:jsonGenerated", { detail: data }));
    };

    const rowHasError = (row: number) => {
        return errors.some((error: TableResult) => error.line === row && error.result === "error");
    };

    const rowHasSuccess = (row: number) => {
        return errors.some((error: TableResult) => error.line === row && error.result === "success");
    };

    const getCellClass = (rowIndex: number, info: string) => {
        if (rowHasError(rowIndex)) return "bg-red-100";
        if (rowHasSuccess(rowIndex)) return "bg-blue-400";
        return info ? "bg-white" : "bg-gray-100";
    };

    const getRowErrors = (row: number): string[] => {
        return errors
            .filter((error) => error.line === row && error.result === "error")
            .map((error) => error.message);
    };

    return (
        <>
            {(tableData.length === 0 || appendMode) && (
                <textarea
                    className="w-full p-2 border border-gray-300 rounded"
                    rows={5}
                    placeholder={appendMode ? "Cole aqui a próxima operação..." : "Cole os dados do Excel aqui..."}
                    value={pasteBuffer}
                    onChange={(event) => setPasteBuffer(event.target.value)}
                    onPaste={handlePaste}
                />
            )}

            {tableData.length > 0 && (

                <div className="overflow-x-scroll scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="min-w-max">

                        <div className="flex sticky top-0 z-10 bg-white shadow-md">
                            {Array.from({ length: maxColumns }).map((_, colIndex) => (
                                <div
                                    key={colIndex}
                                    className={`flex-1 text-center text-xs p-2 relative cursor-move min-w-[150px] transition-all ${draggingColumn === colIndex ? 'bg-blue-300' : ''}`}
                                    draggable
                                    onDragStart={() => handleDragStart(colIndex)}
                                    onDragOver={(event) => handleDragOver(event, colIndex)}
                                    onDragEnd={handleDragEnd}
                                >
                                    {headers[colIndex] ? (
                                        <div className={`bg-blue-500 text-white p-2 rounded ${headers[colIndex].required ? 'font-bold' : 'font-normal'}`}>
                                            {headers[colIndex].label || "..."}
                                        </div>
                                    ) : (
                                        <div className="text-white p-2 rounded">...</div>
                                    )}
                                    <div className="flex justify-center mt-2">
                                        <button
                                            className="text-red-500 text-lg hover:text-red-700"
                                            onClick={() => removeColumn(colIndex)}
                                        >
                                            <HiOutlineTrash />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        < div className="overflow-y-auto" style={{ maxHeight: tableHeight }}>
                            {tableData.map((row, rowIndex) => {
                                const rowErrorMessages = getRowErrors(rowIndex);
                                return (
                                    <div key={rowIndex} className="flex items-center" title={rowErrorMessages.join("\n")}>
                                        {Array.from({ length: maxColumns }).map((_, colIndex) => (
                                            <div key={colIndex}
                                                className={`whitespace-nowrap overflow-x-auto scrollbar-thin flex-1 text-center text-xs p-2 border border-collapse min-w-[150px] ${getCellClass(rowIndex, row[colIndex])}`}
                                            >
                                                {row[colIndex] || "-"}
                                            </div>
                                        ))}

                                        <NewTooltip content="Remover linha" alignment="left">
                                            <button
                                                disabled={rowHasSuccess(rowIndex)}
                                                className="text-red-500 ml-2 text-lg disabled:opacity-50"
                                                onClick={() => removeRow(rowIndex)}
                                            >
                                                <HiOutlineTrash />
                                            </button>
                                        </NewTooltip>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="p-3 bg-gray-100 text-center font-bold shadow-[inset_0px_5px_15px_-3px_rgba(0,_0,_0,_0.2)]">
                            Linhas: {tableData.length} | Colunas: {tableData[0].length || 0}
                            {errors.length > 0 ? <span className="text-red-600 ml-2">{errors.length} erro(s) encontrado(s)</span> : <span className="ml-2 text-thin">Nennhum erro encontrado</span>}
                        </div>
                    </div>
                </div>

            )}
        </>
    );

};

export default CopyPasteTable;